/**
 * Main application component with routing
 */
import { lazy } from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { QueryClientProvider } from '@tanstack/react-query';
import { queryClient } from '@/config/queryClient';
import { AppShell, LanguageParamHandler, LazyRoute, MainLayout, OAuthTokenHandler } from '@/components/layout';
import { SiteRedirect } from '@/components/features';
import { HomePage, LoginPage, RegisterPage, ForgotPasswordPage, ResetPasswordPage, ConfirmEmailPage, SessionsPage, AcousticPage, SessionDetailPage, PricingPage, FavoritesPage, HistoryPage, ProfilePage, PaymentSuccessPage, PendulumPage, JournalPage, ChakraPage, NotFoundPage } from '@/pages';
import { ROUTES } from '@/config/routes';
import { isAcousticEnabled } from '@/config/features';

// The book pages load on demand with their styles and font (ebook-plan § 3.13, § 5.7 budget).
const BookPage = lazy(() => import('@/pages/book/BookPage'));
const BookReaderPage = lazy(() => import('@/pages/book/BookReaderPage'));
const BookSuccessPage = lazy(() => import('@/pages/book/BookSuccessPage'));

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <OAuthTokenHandler />
        <LanguageParamHandler />
        <Routes>
          <Route element={<AppShell />}>
            <Route element={<MainLayout />}>
              {/* Public routes */}
              <Route path={ROUTES.home} element={<HomePage />} />
              <Route path={ROUTES.login} element={<LoginPage />} />
              <Route path={ROUTES.register} element={<RegisterPage />} />
              <Route path="/forgot-password" element={<ForgotPasswordPage />} />
              <Route path="/reset-password" element={<ResetPasswordPage />} />
              <Route path="/confirm-email" element={<ConfirmEmailPage />} />

              {/* Session routes: /sessions = Vođene; /acoustic = Akustične (build flag, off by default) */}
              <Route path={ROUTES.guided} element={<SessionsPage />} />
              <Route path={`${ROUTES.guided}/:sessionId`} element={<SessionDetailPage />} />
              {isAcousticEnabled() && <Route path={ROUTES.acoustic} element={<AcousticPage />} />}
              <Route path={ROUTES.subscription} element={<PricingPage />} />
              <Route path={ROUTES.paymentSuccess} element={<PaymentSuccessPage />} />
              <Route path={ROUTES.favorites} element={<FavoritesPage />} />
              <Route path={ROUTES.history} element={<HistoryPage />} />
              <Route path={ROUTES.profile} element={<ProfilePage />} />

              {/* Premium features (Pendulum is reached from Profil → Alati; Chakra has no link) */}
              <Route path={ROUTES.pendulum} element={<PendulumPage />} />
              <Route path={ROUTES.journal} element={<JournalPage />} />
              <Route path={ROUTES.chakra} element={<ChakraPage />} />

              {/* In-app book overview; shown only when GET /api/book says visible (else 404) */}
              <Route path={ROUTES.book} element={<LazyRoute><BookPage /></LazyRoute>} />
              {/* Stripe Checkout returns here after the e-book payment (§ 3.9); success only on `owned` */}
              <Route path={ROUTES.bookSuccess} element={<LazyRoute><BookSuccessPage /></LazyRoute>} />

              {/* These pages live on lukaaji.com: Terms and Privacy (S5a), the rest since SEO APP-03 */}
              <Route path="/privacy" element={<SiteRedirect page="privacy" titleKey="privacyPolicy" />} />
              <Route path="/terms" element={<SiteRedirect page="terms" titleKey="termsOfService" />} />
              <Route path="/about" element={<SiteRedirect page="method" titleKey="about" />} />
              <Route path="/contact" element={<SiteRedirect page="contact" titleKey="contact" />} />
              <Route path="/faq" element={<SiteRedirect page="faq" titleKey="faq" />} />
              <Route path="/help" element={<SiteRedirect page="faq" titleKey="helpCenter" />} />

              {/* 404 */}
              <Route path="*" element={<NotFoundPage />} />
            </Route>

            {/* The book reader has its own layout: no app header or bottom nav, one <main> (CR-6) */}
            <Route path={ROUTES.bookRead} element={<LazyRoute><BookReaderPage /></LazyRoute>} />
          </Route>
        </Routes>
      </BrowserRouter>
    </QueryClientProvider>
  );
}

export default App;
